import {useState, useCallback} from "react";
import {ActionedRecord, BatchProgress, BucketData, BucketFilters, ImbalanceItem} from "../data/types";
import {adjUnits, skuKey} from "../data/imbalanceHelpers";
import {toManualAdjustments} from "../data/adjustmentMapper";
import {imbalanceService} from "../../components/shared/ImbalanceService";

type UseBucketActionsResult = {
    bucketData: BucketData;
    bucketFilters: BucketFilters;
    actionedSkus: Record<string, ActionedRecord>;
    totalBucketCount: number;
    batchProgress: Record<string, BatchProgress>;
    clearBatchProgress: (key: string) => void;
    setBucketFilter: (key: 'zero' | 'under' | 'over', val: string) => void;
    suggestCleanups: () => void;
    performAction: (key: 'zero' | 'under' | 'over', selectedItems: ImbalanceItem[]) => Promise<{failedItems: number}>;
};

export function useBucketActions(
    imbalances: ImbalanceItem[],
    onSwitchToCleanups: () => void,
): UseBucketActionsResult {
    const [bucketData, setBucketData] = useState<BucketData>({zero: [], under: [], over: []});
    const [bucketFilters, setBucketFilters] = useState<BucketFilters>({zero: '', under: '', over: ''});
    const [actionedSkus, setActionedSkus] = useState<Record<string, ActionedRecord>>({});
    const [batchProgress, setBatchProgress] = useState<Record<string, BatchProgress>>({});

    const totalBucketCount = bucketData.zero.length + bucketData.under.length + bucketData.over.length;

    const clearBatchProgress = useCallback((key: string) => {
        setBatchProgress(prev => {
            const next = {...prev};
            delete next[key];
            return next;
        });
    }, []);

    const setBucketFilter = useCallback((key: 'zero' | 'under' | 'over', val: string) => {
        setBucketFilters(prev => ({...prev, [key]: val}));
    }, []);

    const suggestCleanups = useCallback(() => {
        const eligible = imbalances.filter(item =>
            item.persistentImbalance !== 0 &&
            item.cleanStatus !== 'SUBMITTED' &&
            !actionedSkus[skuKey(item)]
        );

        const top1k = (arr: ImbalanceItem[]) =>
            [...arr].sort((a, b) => adjUnits(b) - adjUnits(a)).slice(0, 1000);

        setBucketData({
            zero: top1k(eligible.filter(item => item.direction === 'WMS_0')),
            under: top1k(eligible.filter(item => item.direction === 'WMS_LESS_THAN_CI')),
            over: top1k(eligible.filter(item => item.direction === 'WMS_MORE_THAN_CI')),
        });
        setBucketFilters({zero: '', under: '', over: ''});
        onSwitchToCleanups();
    }, [imbalances, actionedSkus, onSwitchToCleanups]);

    const performAction = useCallback(async (key: 'zero' | 'under' | 'over', selectedItems: ImbalanceItem[]): Promise<{failedItems: number}> => {
        if (selectedItems.length === 0) return {failedItems: 0};

        const adjType = key === 'over' ? 'POSADJ' : 'NEGADJ';
        const lcLabel = key === 'zero' ? 'LC: Yes' : key === 'under' ? 'LC: No' : 'Bonded';

        setActionedSkus(prev => {
            const next = {...prev};
            selectedItems.forEach(item => {
                next[skuKey(item)] = {status: 'processing', adjType, units: adjUnits(item), lc: lcLabel, bucket: key};
            });
            return next;
        });

        setBatchProgress(prev => ({
            ...prev,
            [key]: {completedItems: 0, totalItems: selectedItems.length, failedItems: 0, percentage: 0, errors: [], done: false},
        }));

        try {
            const adjustments = toManualAdjustments(selectedItems, key);

            const {failedBatches} = await imbalanceService.submitManualAdjustmentsBatched(
                adjustments,
                (completedItems, totalItems) => {
                    setBatchProgress(prev => ({
                        ...prev,
                        [key]: {
                            ...prev[key],
                            completedItems,
                            totalItems,
                            percentage: Math.round((completedItems / totalItems) * 100),
                        },
                    }));
                },
            );

            const ref = `${adjType}-${Date.now().toString(36).toUpperCase().slice(-6)}`;
            const ts = new Date().toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            });

            const failedIndexSet = new Set(
                failedBatches.flatMap(b => {
                    const indices: number[] = [];
                    for (let i = b.startIdx; i < b.endIdx; i++) indices.push(i);
                    return indices;
                })
            );

            setActionedSkus(prev => {
                const next = {...prev};
                selectedItems.forEach((item, i) => {
                    const k = skuKey(item);
                    if (failedIndexSet.has(i)) {
                        delete next[k];
                    } else if (next[k]) {
                        next[k] = {...next[k], status: 'processed', ref, ts};
                    }
                });
                return next;
            });

            const batchErrors = failedBatches.map(b => ({
                batchIndex: b.batchIndex,
                itemRange: [b.startIdx, b.endIdx] as [number, number],
                error: b.error,
            }));

            setBatchProgress(prev => ({
                ...prev,
                [key]: {
                    completedItems: selectedItems.length,
                    totalItems: selectedItems.length,
                    failedItems: failedIndexSet.size,
                    percentage: 100,
                    errors: batchErrors,
                    done: true,
                },
            }));

            return {failedItems: failedIndexSet.size};
        } catch (err) {
            setActionedSkus(prev => {
                const next = {...prev};
                selectedItems.forEach(item => {
                    delete next[skuKey(item)];
                });
                return next;
            });
            setBatchProgress(prev => ({
                ...prev,
                [key]: {
                    ...prev[key],
                    failedItems: selectedItems.length,
                    percentage: 100,
                    errors: [{batchIndex: -1, itemRange: [0, selectedItems.length], error: String(err)}],
                    done: true,
                },
            }));
            throw err;
        }
    }, []);

    return {
        bucketData, bucketFilters, actionedSkus, totalBucketCount, batchProgress, clearBatchProgress,
        setBucketFilter, suggestCleanups, performAction,
    };
}
